import * as React from "react";
import { Button, Dialog, Textarea } from "@/components/ui";
import { BackgroundAsset, BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { BackgroundTileCrop } from "./BackgroundTileCrop";

export interface RefineBackgroundDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  asset: Pick<BackgroundAsset, "id" | "url" | "focal">;
  ratio: BackgroundRatio;
  /** The tray refines onto its slide; the admin library refines with no package or template. */
  onSubmit(instruction: string): Promise<void>;
  busy: boolean;
}

const MAX_INSTRUCTION = 300;

export const RefineBackgroundDialog: React.FC<RefineBackgroundDialogProps> = ({
  open,
  onOpenChange,
  asset,
  ratio,
  onSubmit,
  busy,
}) => {
  const [instruction, setInstruction] = React.useState("");

  React.useEffect(() => {
    if (open) setInstruction("");
  }, [open]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = instruction.trim();
    if (!trimmed) return;
    try {
      await onSubmit(trimmed);
      onOpenChange(false);
    } catch {
      // The caller already toasted the error; keep the dialog open to try again.
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="sm">
      <Dialog.Header>
        <Dialog.Title>Refine with AI…</Dialog.Title>
        <Dialog.Description>Describe the change. The layout and text never change.</Dialog.Description>
      </Dialog.Header>
      <form id="refine-background" onSubmit={submit}>
        <Dialog.Content className="flex flex-col gap-4">
          <BackgroundTileCrop asset={asset} ratio={ratio} className="mx-auto w-32" />
          <Textarea
            aria-label="Instruction"
            value={instruction}
            onChange={event => setInstruction(event.target.value.slice(0, MAX_INSTRUCTION))}
            maxLength={MAX_INSTRUCTION}
            placeholder="e.g. make it warmer, more empty sky at the top"
            rows={3}
            disabled={busy}
          />
        </Dialog.Content>
      </form>
      <Dialog.Footer>
        <Button type="submit" form="refine-background" loading={busy} disabled={!instruction.trim()}>
          Refine
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
